import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import BookedDelegatesTable from '@/components/bookings/BookedDelegatesTable';
import DelegatePicker from '@/components/bookings/DelegatePicker';
import {
  User,
  Loader2
} from 'lucide-react';

/**
 * DelegatesContent - Manages delegates for all booking statuses
 * - Draft: Uses DelegatePicker with recommendations and auto-assign
 * - Live/Booked: View and manage booked delegates
 */
const DelegatesContent = ({ booking, onDelegateChange, onSaveStateChange }) => {
  const { company } = useAuth();
  const [assignedDelegates, setAssignedDelegates] = useState([]);
  const [delegateCount, setDelegateCount] = useState(0);
  const [initializing, setInitializing] = useState(true);
  const autoSaveTimeoutRef = useRef(null);
  const isInitializedRef = useRef(false);
  const [hasUserModified, setHasUserModified] = useState(false);

  const isDraft = booking?.status === 'draft';
  const isLive = booking?.status === 'live';
  const canEditDelegates = isDraft || isLive;

  // Fetch delegate count for live/booked modes
  useEffect(() => {
    fetchDelegateCount();
  }, [booking?.id]);

  // Initialize assigned delegates by matching delegate_names to space members
  useEffect(() => {
    const initializeAssignedDelegates = async () => {
      if (!canEditDelegates || !booking?.project_id) {
        setInitializing(false);
        return;
      }

      // If no delegate_names saved, nothing to initialize
      if (!booking.delegate_names || booking.delegate_names.length === 0) {
        setInitializing(false);
        return;
      }

      try {
        // Fetch space members to match against
        const { data: spaceMembers, error } = await supabase
          .from('space_members')
          .select(`
            id, user_id,
            user_profiles!space_members_user_id_fkey (
              id, display_name, first_name, last_name, email, avatar_url
            )
          `)
          .eq('project_id', booking.project_id);

        if (error || !spaceMembers) {
          setInitializing(false);
          return;
        }

        // Match delegate_names to space members
        const matched = [];
        for (const name of booking.delegate_names) {
          const member = spaceMembers.find(sm => {
            const profile = sm.user_profiles;
            if (!profile) return false;
            const displayName = profile.display_name ||
              `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
            return displayName.toLowerCase() === name.toLowerCase();
          });

          if (member) {
            const profile = member.user_profiles;
            matched.push({
              id: member.id,
              space_member_id: member.id,
              user_id: member.user_id,
              name: profile.display_name || `${profile.first_name || ''} ${profile.last_name || ''}`.trim(),
              display_name: profile.display_name || `${profile.first_name || ''} ${profile.last_name || ''}`.trim(),
              avatar_url: profile.avatar_url,
              email: profile.email
            });
          }
        }

        setAssignedDelegates(matched);
      } catch (err) {
        console.error('Error initializing delegates:', err);
      } finally {
        setInitializing(false);
      }
    };

    initializeAssignedDelegates();
  }, [booking?.id, booking?.project_id, booking?.delegate_names, canEditDelegates]);

  // Mark as initialized after delegates are loaded
  useEffect(() => {
    if (initializing) return;
    const timer = setTimeout(() => {
      isInitializedRef.current = true;
    }, 500);
    return () => clearTimeout(timer);
  }, [initializing]);

  // Auto-save when assigned delegates change
  useEffect(() => {
    if (!isInitializedRef.current || !hasUserModified || !canEditDelegates) return;

    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    autoSaveTimeoutRef.current = setTimeout(() => {
      handleSave();
    }, 1000);

    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [assignedDelegates, hasUserModified, canEditDelegates]);

  const fetchDelegateCount = async () => {
    if (!booking?.id) return;
    const { count, error } = await supabase
      .from('booking_delegates')
      .select('*', { count: 'exact', head: true })
      .eq('training_quote_id', booking.id);
    if (!error) setDelegateCount(count || 0);
  };

  const handleAddDelegate = useCallback(async (member) => {
    const profile = member.user_profiles || member.user || {};
    const displayName = profile.display_name ||
      `${profile.first_name || ''} ${profile.last_name || ''}`.trim() ||
      'Unknown';

    const newDelegate = {
      id: member.id,
      space_member_id: member.id,
      user_id: member.user_id,
      name: displayName,
      display_name: displayName,
      avatar_url: profile.avatar_url,
      email: profile.email
    };

    setHasUserModified(true);
    setAssignedDelegates(prev => [...prev, newDelegate]);
  }, []);

  const handleRemoveDelegate = useCallback(async (delegate) => {
    setHasUserModified(true);
    setAssignedDelegates(prev => prev.filter(d => d.id !== delegate.id));
  }, []);

  const handleSave = async () => {
    onSaveStateChange?.(true);
    try {
      const delegateNames = assignedDelegates
        .map(d => d.display_name || d.name)
        .filter(Boolean);

      const { error } = await supabase
        .from('training_quotes')
        .update({
          delegates: assignedDelegates.length,
          delegate_names: delegateNames,
          updated_date: new Date().toISOString()
        })
        .eq('id', booking.id);

      if (error) throw error;

      const now = new Date();
      onSaveStateChange?.(false, now);
      if (onDelegateChange) onDelegateChange(assignedDelegates.length);
    } catch (error) {
      console.error('Error saving delegates:', error);
      toast.error('Failed to save delegates');
      onSaveStateChange?.(false);
    }
  };

  // Draft/Live mode - Use DelegatePicker with recommendations
  if (canEditDelegates) {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Select Delegates</h3>
          <p className="text-sm text-gray-500">
            Choose team members who will attend this training. Recommended delegates are shown based on their training requirements.
          </p>
        </div>

        {initializing ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        ) : (
          <DelegatePicker
            booking={booking}
            assignedDelegates={assignedDelegates}
            onAdd={handleAddDelegate}
            onRemove={handleRemoveDelegate}
          />
        )}
      </div>
    );
  }

  // Booked mode - Show booked delegates table
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Delegates</h3>
          <p className="text-sm text-gray-500">
            {delegateCount} delegate{delegateCount !== 1 ? 's' : ''} registered for this training
          </p>
        </div>
      </div>

      {/* Pre-selected delegate names from quote (if no booked delegates yet) */}
      {booking?.delegate_names && booking.delegate_names.length > 0 && delegateCount === 0 && (
        <div className="mb-4">
          <p className="text-sm text-gray-500 mb-2">Requested delegates:</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {booking.delegate_names.map((name, index) => (
              <div
                key={index}
                className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 border border-gray-100"
              >
                <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <User className="h-4 w-4 text-purple-600" />
                </div>
                <span className="text-sm font-medium">{name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Booked Delegates Table */}
      <BookedDelegatesTable
        booking={booking}
        onDelegateChange={() => {
          fetchDelegateCount();
          if (onDelegateChange) onDelegateChange();
        }}
      />
    </div>
  );
};

export default DelegatesContent;
